import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Activity, Bookmark, Factory, Globe, ListChecks, ThumbsUp } from 'lucide-react';

import type { BlocklistResult } from '@ballerine/business-analysis-schemas';
import type { TransformedPointOfSaleReport } from '@ballerine/common';
import type { CustomerFeatures } from '@ballerine/customer-schemas';
import { isMonitoringReportType } from '@ballerine/common';

import type { PreviousReportsLinks } from '../../../report-shared/components/PreviousReportsButton/PreviousReportsButton';
import { BlocklistMatchesSection } from '../../../digital-footprint/sections/BlocklistMatches';
import {
  EcosystemSection,
  HeaderSection,
  MerchantDetailsSection,
  OperationalActivitySection,
  ReferencesSection,
  RiskAnalysisSection,
  SocialMediaSection,
  TagsSection,
} from '../../sections';

type PointOfSaleReportSection = {
  id: string;
  title: string;
  description?: string;
  Icon?: LucideIcon;
  Component: ReactNode;
  hasViolations?: boolean;
  isPremium?: boolean;
  excludeFromObserver?: boolean;
  isHidden: boolean;
};

export const usePointOfSaleReportSections = (
  report: TransformedPointOfSaleReport & {
    monitoringStatus?: string | null;
    changesSummary?: string | null;
    isAlert?: boolean | null;
    comparedToReportId?: string | null;
  },
  googleMapsApiKey?: string,
  FeedbackComponent?: React.ComponentType<{ reportId: string }>,
  Link?: React.ComponentType<{
    to: string;
    children: React.ReactNode;
    className?: string;
  }>,
  reportType?: string,
  previousReportsLinks?: PreviousReportsLinks | null,
  customerFeatures?: CustomerFeatures | null,
  locale?: string,
  BusinessDetailsSlot?: React.ComponentType,
) => {
  const {
    providedDetails,
    ecosystem,
    socialMedia,
    synthesizerResult,
    matchReason,
    merchantDetails,
    operationalProof,
    structuredTags,
  } = report.data;

  const blocklist = (report.data as { blocklist?: BlocklistResult | null }).blocklist;
  const visibleBlocklist =
    (customerFeatures?.CUSTOMER_BLOCKLIST?.enabled ?? false) ? (blocklist ?? null) : null;
  const blocklistConfigUrl = locale ? `/${locale}/account-settings` : undefined;
  const {
    references,
    riskTypes,
    riskLevel,
    riskAnalysis,
    tags: synthesizerTags,
  } = synthesizerResult ?? {};
  const { mcc, lineOfBusiness, lineOfBusinessReasoning, alternatives } = operationalProof ?? {};
  const enableAlternativeMccSuggestions =
    customerFeatures?.DIGITAL_FOOTPRINT?.enableAlternativeMccSuggestions ?? false;
  const { facebook, instagram, twitter, linkedin, tiktok } = socialMedia ?? {};

  const isMonitoringReport =
    isMonitoringReportType(reportType) || isMonitoringReportType(report.reportType);
  const ongoingMonitoringSummary = (report as any).changesSummary ?? null;
  const isAlert = (report as any).isAlert ?? false;

  const tags = structuredTags?.length ? structuredTags : synthesizerTags;

  // BAL-7823: extracted details are stale carry-forward in monitoring — header map uses declared.
  const headerMerchantDetails = isMonitoringReport
    ? providedDetails && {
        companyName: providedDetails.companyName ?? null,
        address: providedDetails.address ?? providedDetails.addresses?.[0] ?? null,
        country: providedDetails.country ?? null,
      }
    : merchantDetails;

  const sections = useMemo(() => {
    const allSections = [
      {
        id: 'header',
        Icon: Globe,
        excludeFromObserver: true,
        isHidden: false,
        Component: (
          <HeaderSection
            reportId={report.id}
            riskLevel={riskLevel}
            lineOfBusiness={lineOfBusiness}
            riskTypes={riskTypes}
            isMonitoringReport={isMonitoringReport}
            isAlert={isAlert}
            FeedbackComponent={FeedbackComponent}
            merchantDetails={headerMerchantDetails}
            googleMapsApiKey={googleMapsApiKey}
            reportType={reportType}
            blocklist={visibleBlocklist}
            blocklistConfigUrl={blocklistConfigUrl}
          />
        ),
      },
      {
        id: 'blocklist',
        title: 'Blocklist',
        isHidden: !visibleBlocklist || visibleBlocklist.matches.length === 0,
        Icon: ListChecks,
        excludeFromObserver: true,
        Component: (
          <BlocklistMatchesSection
            blocklist={visibleBlocklist}
            blocklistConfigUrl={blocklistConfigUrl}
          />
        ),
      },
      {
        id: 'tags',
        isHidden: !tags?.length,
        Icon: ListChecks,
        excludeFromObserver: true,
        Component: <TagsSection tags={tags} />,
      },
      {
        id: 'risk-analysis',
        title: 'Risk Analysis',
        isHidden: riskAnalysis == null && !ongoingMonitoringSummary,
        Icon: ListChecks,
        Component: (
          <RiskAnalysisSection
            summary={riskAnalysis}
            createdAt={
              typeof report.createdAt === 'string' ? new Date(report.createdAt) : report.createdAt
            }
            ongoingMonitoringSummary={ongoingMonitoringSummary}
            homepageScreenshotUrl={null}
            previousReportsLinks={previousReportsLinks}
            isAlert={isAlert}
            isMonitoringReport={isMonitoringReport}
          />
        ),
      },
      {
        id: 'business-details',
        title: 'Business Details',
        // BAL-7823: monitoring runs no extraction — declared details replace the stale comparison.
        isHidden: !isMonitoringReport || BusinessDetailsSlot == null,
        Icon: Factory,
        Component: BusinessDetailsSlot ? <BusinessDetailsSlot /> : null,
      },
      {
        id: 'merchant-details',
        title: 'Operating Company Details',
        isHidden: !merchantDetails || isMonitoringReport,
        Icon: Factory,
        Component: (
          <MerchantDetailsSection
            merchantDetails={merchantDetails}
            providedDetails={providedDetails}
            matchReason={matchReason}
            googleMapsApiKey={googleMapsApiKey}
          />
        ),
      },
      {
        id: 'operational-analysis',
        title: 'Operational Activity',
        // Always visible — the section renders N/A placeholders when LOB/MCC are null.
        isHidden: false,
        Icon: Activity,
        Component: (
          <OperationalActivitySection
            lineOfBusiness={lineOfBusiness}
            lineOfBusinessReasoning={lineOfBusinessReasoning}
            mcc={mcc}
            alternatives={alternatives}
            showAlternatives={enableAlternativeMccSuggestions}
          />
        ),
      },
      {
        id: 'ecosystem',
        title: 'Ecosystem',
        isHidden: ecosystem == null,
        Icon: Globe,
        Component: <EcosystemSection ecosystem={ecosystem} />,
      },
      {
        id: 'social-media',
        title: 'Social Media',
        isHidden: socialMedia == null,
        Icon: ThumbsUp,
        Component: (
          <SocialMediaSection
            facebookPage={facebook}
            instagramPage={instagram}
            twitterPage={twitter}
            linkedinPage={linkedin}
            tiktokPage={tiktok}
          />
        ),
      },
      {
        id: 'references',
        title: 'References',
        isHidden: references == null,
        Icon: Bookmark,
        Component: <ReferencesSection references={references} />,
      },
    ] as PointOfSaleReportSection[];

    return allSections.filter(section => !section.isHidden);
  }, [
    report.data,
    report.id,
    report.createdAt,
    report.reportType,
    reportType,
    googleMapsApiKey,
    FeedbackComponent,
    Link,
    isMonitoringReport,
    ongoingMonitoringSummary,
    isAlert,
    previousReportsLinks,
    visibleBlocklist,
    blocklistConfigUrl,
    BusinessDetailsSlot,
  ]);

  return { sections };
};
